/* Jam scoring. Every bar is one chord note. A bar opens shortly before its downbeat and stays open until
   the next one opens; in that window the right chord, heard steadily, is a hit, and a different chord,
   heard steadily, is a wrong note. A bar that closes without either is a miss. Hits in a row raise the
   multiplier (×1 → ×2 → ×4 → ×8); a wrong note or a miss drops it back to ×1 at once.
   Pure logic: the page feeds chord frames (or screen taps) in song time and draws the events. */
import type { ChordQuality, ChordRef } from '../dsp/chord';
import type { JamChart } from './chart';

/** Points for a hit at ×1 and full tempo. */
export const BASE_POINTS = 100;
/** Hits in a row per multiplier step: 4 → ×2, 8 → ×4, 12 → ×8. */
export const MULT_STEP = 4;
export const MAX_MULT = 8;
/** A bar opens this long before its downbeat, for players who change chords a little early (s). */
export const EARLY = 0.15;
/** The last bar stays open this long after the song ends: chord detection lags the strum (s). */
export const LATE = 0.35;
/** The previous bar's chord still rings into the next bar; it is not a wrong note this early (s). */
export const PREV_GRACE = 0.7;
/** Chord frames (about 11 per second) that must agree before a bar is judged. */
export const HIT_FRAMES = 3;
export const WRONG_FRAMES = 5;
/** Lowest match score for a frame to count as the right chord, or as a wrong one. */
export const HIT_SCORE = 0.5;
export const WRONG_SCORE = 0.6;

/** Multiplier after `streak` hits in a row. */
export const multFor = (streak: number): number => Math.min(MAX_MULT, 2 ** Math.floor(streak / MULT_STEP));

/** Heard chord qualities that count as playing each written quality (same root). The written chord
    always counts; 7ths, 6ths and add9 count for their triad and the triad for them; a power chord
    counts for a major or minor bar and either for a power chord. */
const ACCEPT: Record<ChordQuality, readonly ChordQuality[]> = {
  '': ['', '7', 'maj7', '6', 'add9', 'sus2', 'sus4', '5'],
  m: ['m', 'm7', 'm6', '5'],
  '7': ['7', ''],
  maj7: ['maj7', ''],
  m7: ['m7', 'm'],
  dim: ['dim', 'dim7', 'm7b5'],
  aug: ['aug', ''],
  sus2: ['sus2', '', '5'],
  sus4: ['sus4', '', '5'],
  m7b5: ['m7b5', 'dim'],
  '6': ['6', ''],
  m6: ['m6', 'm'],
  '5': ['5', '', 'm'],
  dim7: ['dim7', 'dim'],
  add9: ['add9', ''],
};

/** Does hearing `heard` count as playing `target`? */
export const compatible = (heard: ChordRef, target: ChordRef): boolean =>
  heard.root === target.root && ACCEPT[target.q].includes(heard.q);

export type JamMark = 'hit' | 'wrong' | 'miss';

export interface HeardChord extends ChordRef {
  score: number;
}

/** One chord frame from the microphone, in song time. */
export interface JamFrame {
  /** seconds from the first downbeat (negative during the count-in) */
  t: number;
  /** loud enough and not gated */
  active: boolean;
  /** best match in the song's vocabulary */
  top: HeardChord | null;
}

export type JamEvent =
  | { type: 'hit'; bar: number; t: number; points: number; mult: number; streak: number }
  | { type: 'wrong'; bar: number; t: number; heard: ChordRef }
  | { type: 'miss'; bar: number; t: number }
  /** the multiplier changed: up after a hit, back to ×1 after a wrong note or a miss */
  | { type: 'mult'; bar: number; mult: number; prev: number }
  | { type: 'end' };

export interface JamResult {
  score: number;
  hits: number;
  wrongs: number;
  misses: number;
  /** bars in the song, and bars judged so far */
  total: number;
  judged: number;
  /** hits / total (hits / judged for a song stopped early) */
  acc: number;
  bestStreak: number;
  maxMult: number;
  stars: number;
  marks: (JamMark | null)[];
  aborted: boolean;
}

export const starsFor = (acc: number): number => (acc >= 0.9 ? 3 : acc >= 0.75 ? 2 : acc >= 0.5 ? 1 : 0);

/** Score for hitting every one of `bars` bars. */
export function maxScore(bars: number, base = BASE_POINTS): number {
  let s = 0;
  for (let i = 0; i < bars; i++) s += Math.round(base * multFor(i));
  return s;
}

export class JamScorer {
  readonly marks: (JamMark | null)[];
  score = 0;
  hits = 0;
  wrongs = 0;
  misses = 0;
  streak = 0;
  bestStreak = 0;
  mult = 1;
  maxMult = 1;
  ended = false;
  /** first bar whose window has not closed yet */
  private next = 0;
  private runBar = -1;
  private hitRun = 0;
  private wrongRun = 0;
  private wrongKey = '';

  /** `base` points per hit at ×1 (the page scales it with the tempo). */
  constructor(
    readonly chart: JamChart,
    readonly base = BASE_POINTS,
  ) {
    this.marks = chart.notes.map(() => null);
  }

  /** Song time at which bar `i` opens. */
  opens(i: number): number {
    return (this.chart.notes[i]?.t ?? 0) - EARLY;
  }

  /** Song time at which bar `i` closes (the next bar opens; the last one closes after the song). */
  closes(i: number): number {
    const n = this.chart.notes;
    return i + 1 < n.length ? this.opens(i + 1) : (n[i]?.end ?? 0) + LATE;
  }

  /** Bar open at song time `t`, or -1 (count-in, or after the end). */
  barAt(t: number): number {
    const n = this.chart.notes.length;
    if (!n || t < this.opens(0) || t >= this.closes(n - 1)) return -1;
    const i = Math.floor((t + EARLY) / this.chart.barLen);
    return Math.max(0, Math.min(n - 1, i));
  }

  /** Closes every bar whose window ended by `t`: a bar nobody played is a miss. */
  advance(t: number): JamEvent[] {
    const out: JamEvent[] = [];
    const n = this.chart.notes.length;
    while (this.next < n && t >= this.closes(this.next)) {
      if (this.marks[this.next] == null) this.judge(this.next, 'miss', this.closes(this.next), out);
      this.next++;
    }
    if (this.next >= n && !this.ended) {
      this.ended = true;
      out.push({ type: 'end' });
    }
    return out;
  }

  /** A chord frame from the microphone. */
  push(f: JamFrame): JamEvent[] {
    const out = this.advance(f.t);
    const i = this.barAt(f.t);
    if (i < 0 || this.marks[i] != null) return out;
    if (i !== this.runBar) {
      this.runBar = i;
      this.hitRun = 0;
      this.wrongRun = 0;
      this.wrongKey = '';
    }
    const top = f.top;
    if (!f.active || !top) return out;
    const note = this.chart.notes[i];
    if (!note) return out;
    if (compatible(top, note.ref)) {
      if (top.score < HIT_SCORE) return out;
      this.wrongRun = 0;
      if (++this.hitRun >= HIT_FRAMES) this.judge(i, 'hit', f.t, out);
      return out;
    }
    const prev = this.chart.notes[i - 1];
    if (prev && f.t - note.t < PREV_GRACE && compatible(top, prev.ref)) return out;
    if (top.score < WRONG_SCORE) return out;
    this.hitRun = Math.max(0, this.hitRun - 1);
    const key = `${top.root}|${top.q}`;
    this.wrongRun = key === this.wrongKey ? this.wrongRun + 1 : 1;
    this.wrongKey = key;
    if (this.wrongRun >= WRONG_FRAMES) this.judge(i, 'wrong', f.t, out, { root: top.root, q: top.q });
    return out;
  }

  /** A chord chosen on screen: judged at once. */
  tap(t: number, chord: ChordRef): JamEvent[] {
    const out = this.advance(t);
    const i = this.barAt(t);
    const note = this.chart.notes[i];
    if (!note || this.marks[i] != null) return out;
    this.judge(i, compatible(chord, note.ref) ? 'hit' : 'wrong', t, out, chord);
    return out;
  }

  get judged(): number {
    return this.hits + this.wrongs + this.misses;
  }

  result(aborted = false): JamResult {
    const total = this.chart.notes.length;
    const judged = this.judged;
    const acc = (aborted ? this.hits / Math.max(1, judged) : this.hits / Math.max(1, total)) || 0;
    return {
      score: this.score,
      hits: this.hits,
      wrongs: this.wrongs,
      misses: this.misses,
      total,
      judged,
      acc,
      bestStreak: this.bestStreak,
      maxMult: this.maxMult,
      stars: aborted ? 0 : starsFor(acc),
      marks: [...this.marks],
      aborted,
    };
  }

  private judge(i: number, mark: JamMark, t: number, out: JamEvent[], heard?: ChordRef): void {
    this.marks[i] = mark;
    const before = this.mult;
    if (mark === 'hit') {
      const points = Math.round(this.base * this.mult);
      this.score += points;
      this.hits++;
      this.streak++;
      this.bestStreak = Math.max(this.bestStreak, this.streak);
      this.mult = multFor(this.streak);
      this.maxMult = Math.max(this.maxMult, this.mult);
      out.push({ type: 'hit', bar: i, t, points, mult: before, streak: this.streak });
    } else {
      if (mark === 'wrong') {
        this.wrongs++;
        out.push({ type: 'wrong', bar: i, t, heard: heard ?? { root: -1, q: '' } });
      } else {
        this.misses++;
        out.push({ type: 'miss', bar: i, t });
      }
      this.streak = 0;
      this.mult = 1;
    }
    if (this.mult !== before) out.push({ type: 'mult', bar: i, mult: this.mult, prev: before });
  }
}
